'use client';
import React, { useState } from 'react';
import { Search, ArrowRight, ChevronRight, BookOpen, Star, Users, Clock } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface Course {
  id: string;
  title: string;
  provider: string;
  category: string;
  level: string;
  price: number;
  rating: number;
  students: number;
  duration: string;
  location: string;
  mode: string;
}

const COURSES: Course[] = [
  { id: '1', title: 'Python Programming for Beginners', provider: 'Moringa School', category: 'Technology', level: 'Beginner', price: 45000, rating: 4.8, students: 1240, duration: '3 months', location: 'Nairobi', mode: 'In-Person' },
  { id: '2', title: 'CPA Kenya — Foundation Level', provider: 'KASNEB', category: 'Finance', level: 'Beginner', price: 12000, rating: 4.6, students: 3400, duration: '6 months', location: 'Nationwide', mode: 'Online' },
  { id: '3', title: 'Digital Marketing Masterclass', provider: 'Strathmore University', category: 'Marketing', level: 'Intermediate', price: 35000, rating: 4.7, students: 890, duration: '2 months', location: 'Nairobi', mode: 'Hybrid' },
  { id: '4', title: 'Electrical Installation & Wiring', provider: 'Kenya Polytechnic', category: 'Technical', level: 'Beginner', price: 18000, rating: 4.5, students: 560, duration: '4 months', location: 'Nairobi', mode: 'In-Person' },
  { id: '5', title: 'Data Science with R & Python', provider: 'Andela Learning', category: 'Technology', level: 'Advanced', price: 80000, rating: 4.9, students: 340, duration: '6 months', location: 'Remote', mode: 'Online' },
  { id: '6', title: 'Hospitality & Hotel Management', provider: 'Kenya Utalii College', category: 'Hospitality', level: 'Beginner', price: 55000, rating: 4.4, students: 780, duration: '1 year', location: 'Nairobi', mode: 'In-Person' },
];

const CATEGORIES = ['All', 'Technology', 'Finance', 'Marketing', 'Technical', 'Hospitality'];
const MODES = ['All Modes', 'Online', 'In-Person', 'Hybrid'];
const LEVELS = ['All Levels', 'Beginner', 'Intermediate', 'Advanced'];

export default function EducationPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [mode, setMode] = useState('All Modes');
  const [level, setLevel] = useState('All Levels');

  const filtered = COURSES.filter(c => {
    const matchSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.provider.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || c.category === category;
    const matchMode = mode === 'All Modes' || c.mode === mode;
    const matchLevel = level === 'All Levels' || c.level === level;
    return matchSearch && matchCat && matchMode && matchLevel;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #0d1a0d 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #00BCD4 0%, transparent 50%), radial-gradient(circle at 70% 30%, #7C4DFF 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(0,188,212,0.15)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.3)' }}>
              <BookOpen size={12} /> 5,000+ Courses Available
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Advance Your <span style={{ color: '#00BCD4' }}>Education</span> & Skills
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Courses, certifications, and training programs from top institutions across Kenya.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search courses or institutions…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: category === c ? 'rgba(0,188,212,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#00BCD4' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(0,188,212,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
          </div>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pt-2">
            {MODES.map(m => (
              <button key={m} onClick={() => setMode(m)}
                className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: mode === m ? 'rgba(124,77,255,0.15)' : 'rgba(255,255,255,0.04)', color: mode === m ? '#7C4DFF' : 'rgba(255,255,255,0.45)', border: `1px solid ${mode === m ? 'rgba(124,77,255,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {m}
              </button>
            ))}
            {LEVELS.map(l => (
              <button key={l} onClick={() => setLevel(l)}
                className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: level === l ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: level === l ? '#00C853' : 'rgba(255,255,255,0.45)', border: `1px solid ${level === l ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#00BCD4', fontWeight: 700 }}>{filtered.length}</span> courses found
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#00BCD4', color: '#000' }}>
              List a Course <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map(course => (
              <div key={course.id} className="p-4 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(0,188,212,0.12)', border: '1px solid rgba(0,188,212,0.2)' }}>
                    <BookOpen size={18} style={{ color: '#00BCD4' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-700 text-white leading-tight">{course.title}</h3>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{course.provider}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Star size={10} fill="#FFB300" style={{ color: '#FFB300' }} />
                        <span className="text-[10px] font-700" style={{ color: '#FFB300' }}>{course.rating}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{course.students.toLocaleString()} students</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{course.duration}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm font-800" style={{ color: '#00BCD4' }}>KES {course.price.toLocaleString()}</span>
                      <Link href="/login"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                        style={{ background: 'rgba(0,188,212,0.15)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.25)' }}>
                        Enrol <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(124,77,255,0.1))', border: '1px solid rgba(0,188,212,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">Offer Training or Courses</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>List your institution or training program and reach thousands of learners.</p>
            <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#00BCD4', color: '#000' }}>
              <BookOpen size={16} /> List a Course
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
